/*─────────────────────────────────────────────────────────────────────────────
  SessionStoreService
  ─────────────────────────────────────────────────────────────────────────────
  • Holds the phone number and “isVerified” flag for the two-step OTP flow.
  • Stores the logged-in user object returned by /api/otp/verify.
  • Uses Angular Signals → no extra state library required.
  • Exposes RxJS Observables via toObservable() when streams are handy
    (e.g. route-guards that require an Observable<boolean>).
─────────────────────────────────────────────────────────────────────────────*/
// src/app/core/services/session.store.service.ts
import { Injectable, signal, computed, effect } from '@angular/core';
import { VerifiedUser } from '../../features/auth/phone-capture/phone-capture.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { PasskeyService } from '../../features/passkey/services/passkey.service';
import { PasskeyStoreService } from '../../features/passkey/services/passkey-store.service';
interface PersistedSession {
  phone: string | null;
  verified: boolean;
  user: VerifiedUser | null;
}

const LS_KEY = 'axon-session';

@Injectable({ providedIn: 'root' })
export class SessionStoreService {
  /* writable signals */
  private readonly _phone    = signal<string | null>(null);
  private readonly _verified = signal(false);
  private readonly _user     = signal<VerifiedUser | null>(null);
  private readonly _hasPasskey = signal(false);

  /* read-only signals */
  readonly phone      = computed(() => this._phone());
  readonly isVerified = computed(() => this._verified());
  readonly user       = computed(() => this._user());
  readonly hasPasskey          = computed(() => this._hasPasskey());

  /* RxJS wrappers (optional) */
  readonly isVerified$: Observable<boolean>       = toObservable(this.isVerified);

  /* ───────────────── constructor loads persisted state ───────────────── */
  constructor(private pksvc: PasskeyService,passStore:PasskeyStoreService) {
      /* async init is okay: we just set a flag */
  passStore.load().then(cred => {
    if (cred) this._hasPasskey.set(true);
  });
    if (this.pksvc.hasPasskey()) {
      this._hasPasskey.set(true);
    }
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      try {
        const s: PersistedSession = JSON.parse(raw);
        this._phone.set(s.phone);
        this._verified.set(s.verified);
        this._user.set(s.user);
      } catch {
        console.warn('Bad session JSON — cleared.');
        localStorage.removeItem(LS_KEY);
      }
    }

    /* auto-save whenever any signal changes */
    effect(() => {
      const snapshot: PersistedSession = {
        phone: this._phone(),
        verified: this._verified(),
        user: this._user()
      };
      localStorage.setItem(LS_KEY, JSON.stringify(snapshot));
    });
  }

  /* mutators */
  setPhone(n: string)               { this._phone.set(n); }
  markVerified(u: VerifiedUser)     { this._verified.set(true); this._user.set(u); }

  reset(): void {
    this._phone.set(null);
    this._verified.set(false);
    this._user.set(null);
    localStorage.removeItem(LS_KEY);
  }

  resetSession(): void {
    // wipe transient OTP state but keep passkey flag + stored credential
    this._phone.set(null);
    this._verified.set(false);
    /* do NOT touch _hasPasskey or localStorage passkey */
  }


  /** use only if the user wants to delete their passkey as well */
resetAll(): void {
  this._phone.set(null);
  this._verified.set(false);
  this._hasPasskey.set(false);

  localStorage.removeItem('axon-passkey');   // delete the stored credential
  localStorage.removeItem('axon-cooldown');  // clear any running OTP timer
}

markHasPasskey(): void {
  this._hasPasskey.set(true);
}
}
